import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Upload, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TIMEZONES = ["Asia/Jakarta", "Asia/Kuala_Lumpur", "Asia/Dubai", "Asia/Riyadh", "Europe/London", "America/New_York", "UTC"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef();

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, logo_media_id: data.id }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        store_name: s.store_name, logo_media_id: s.logo_media_id, theme: s.theme,
        timezone: s.timezone, branch: s.branch, ticker_text: s.ticker_text,
        prayer_city: s.prayer_city, prayer_country: s.prayer_country, prayer_method: Number(s.prayer_method) || 20,
        show_prayer_times: s.show_prayer_times, show_kajian: s.show_kajian, show_kas: s.show_kas,
        show_adzan: s.show_adzan, adzan_duration: Number(s.adzan_duration) || 5,
      });
      toast.success("Settings saved & pushed to all screens");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-primary">Configuration</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Settings</h1>
      </div>

      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
            {s.logo_media_id ? <img src={mediaUrl(s.logo_media_id)} className="w-full h-full object-contain" alt="logo" /> : <span className="text-xs text-muted-foreground">No logo</span>}
          </div>
          <div>
            <Label>Logo Masjid</Label>
            <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="settings-logo-input" />
            <div className="mt-2">
              <Button variant="secondary" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="settings-logo-upload" className="gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Logo
              </Button>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Nama Masjid</Label>
            <Input data-testid="settings-store-name" value={s.store_name || ""} onChange={(e) => setS({ ...s, store_name: e.target.value })} className="mt-2 bg-background" />
          </div>
          <div>
            <Label>Cabang / Lokasi</Label>
            <Input data-testid="settings-branch" value={s.branch || ""} onChange={(e) => setS({ ...s, branch: e.target.value })} className="mt-2 bg-background" />
          </div>
          <div>
            <Label>Timezone</Label>
            <Select value={s.timezone} onValueChange={(v) => setS({ ...s, timezone: v })}>
              <SelectTrigger className="mt-2 bg-background" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Theme</Label>
            <Select value={s.theme} onValueChange={(v) => setS({ ...s, theme: v })}>
              <SelectTrigger className="mt-2 bg-background" data-testid="settings-theme"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Terang (Hijau/Putih)</SelectItem>
                <SelectItem value="dark">Gelap</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Running Text (Ticker)</Label>
          <Textarea data-testid="settings-ticker" value={s.ticker_text || ""} onChange={(e) => setS({ ...s, ticker_text: e.target.value })} className="mt-2 bg-background" rows={3} placeholder="Text scrolling at the bottom of every screen" />
        </div>

        <div className="border-t border-border pt-6 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Jadwal Shalat & Layar</p>
            <h2 className="font-display text-lg font-semibold mt-1">Jadwal Shalat Otomatis</h2>
            <p className="text-sm text-muted-foreground">Waktu shalat diambil otomatis (metode Kemenag RI) berdasarkan kota di bawah.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Kota / Kabupaten</Label>
              <Input data-testid="settings-prayer-city" value={s.prayer_city || ""} onChange={(e) => setS({ ...s, prayer_city: e.target.value })} className="mt-2 bg-background" placeholder="mis. Jakarta, Aceh Tamiang" />
            </div>
            <div>
              <Label>Negara</Label>
              <Input data-testid="settings-prayer-country" value={s.prayer_country || ""} onChange={(e) => setS({ ...s, prayer_country: e.target.value })} className="mt-2 bg-background" placeholder="Indonesia" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
              <div>
                <p className="font-medium text-sm">Tampilkan Jadwal Shalat di Layar</p>
                <p className="text-xs text-muted-foreground">Bar waktu shalat + hitung mundur di TV</p>
              </div>
              <Switch data-testid="settings-show-prayer" checked={!!s.show_prayer_times} onCheckedChange={(v) => setS({ ...s, show_prayer_times: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
              <div>
                <p className="font-medium text-sm">Tampilkan Jadwal Pengajian di Layar</p>
                <p className="text-xs text-muted-foreground">Slide daftar pengajian rutin</p>
              </div>
              <Switch data-testid="settings-show-kajian" checked={!!s.show_kajian} onCheckedChange={(v) => setS({ ...s, show_kajian: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
              <div>
                <p className="font-medium text-sm">Tampilkan Laporan Kas di Layar</p>
                <p className="text-xs text-muted-foreground">Slide ringkasan saldo, pemasukan & pengeluaran</p>
              </div>
              <Switch data-testid="settings-show-kas" checked={!!s.show_kas} onCheckedChange={(v) => setS({ ...s, show_kas: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
              <div>
                <p className="font-medium text-sm">Notifikasi Adzan Otomatis</p>
                <p className="text-xs text-muted-foreground">Overlay "Waktu Shalat Telah Tiba" saat masuk waktu shalat</p>
              </div>
              <Switch data-testid="settings-show-adzan" checked={!!s.show_adzan} onCheckedChange={(v) => setS({ ...s, show_adzan: v })} />
            </div>
            {s.show_adzan && (
              <div className="rounded-lg border border-border bg-background px-4 py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-sm">Durasi Tampil Notifikasi Adzan</p>
                  <p className="text-xs text-muted-foreground">Berapa menit overlay adzan tampil</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" max="30" data-testid="settings-adzan-duration" value={s.adzan_duration ?? 5} onChange={(e) => setS({ ...s, adzan_duration: e.target.value })} className="w-20 bg-background" />
                  <span className="text-sm text-muted-foreground">menit</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save Settings
        </Button>
      </div>
    </div>
  );
}
